import { Box, SxProps } from '@mui/system';
import React, { useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../../components/formlib/MediumTypography';
import FamilyConcernsPrioritiesandResourcesReport from './FamilyConcernsPrioritiesandResourcesReport';
import DevelopmentSummaryReport from './DevelopmentSummaryReport';
import HealthEligibilitySummaryReport from './HealthEligibilitySummaryReport';
import {
  addOrUpdateIfspReportCompletedDetails,
  downloadIfspReport,
  EmployeeListType,
  getAllEmployeeMinimalList,
  getIfspReportCompletedDetails,
  ifspHeartBeat,
} from '../../../services/ifspClient/IfspApi';
import DailyRoutineReport from './DailyRoutineReport';
import { AlertColor, autocompleteClasses, Grid } from '@mui/material';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import dayjs from 'dayjs';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import ModalPopup from '../../../components/formlib/ModalPopup';
import { ReactComponent as DownloadIcon } from '../../../assets/images/downloadIcon.svg';
import { ApiError, isCustomError } from '../../../services/ApiResponseHandler';
import {
  IfspHeartBeatType,
  updateIfspHeartBeat,
} from '../../../redux/features/ifsp/ifspHeartBeatslice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import AutocompleteSearch from '../../../components/formlib/AutocompleteSearch';
import { employeeRoles, OptionType } from '../../../utils/type';
import { getClientDocumentFileName } from '../../../utils/fileUtils';
import DropdownComponent from '../../../components/formlib/DropdownComponent';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { formatName } from '../../../utils/nameUtils';
import { setIfspDailyRoutineReport } from '../../../redux/features/ifsp/ifspDailyRoutineSlice';
import { setIfspFamilyConcernReport } from '../../../redux/features/ifsp/ifspFamilyConcernSlice';
import { setIfspDevelopmentSummaryReport } from '../../../redux/features/ifsp/ifspDevelopmentSummarySlice';
import { setIfspHealthSummaryReport } from '../../../redux/features/ifsp/ifspHealthSummarySlice';
import {
  resetIfspUnsavedFields,
  updateIfspUnsavedFields,
} from '../../../redux/features/ifsp/ifspUnsavedFieldSlice';

type ReportCompletedByType = {
  id: string | null;
  name: string | null;
  role: string | null;
  reportUpdated: boolean;
};

export type IfspReportCompletedByType = {
  ifspId: string | null;
  reportCompletedBy: ReportCompletedByType;
};

type ActiveTabType =
  | 'Daily-Routine'
  | 'Family-Concern'
  | 'Developmental-Summary'
  | 'Health-Eligibility';

const tabs: { key: ActiveTabType; label: string }[] = [
  { key: 'Daily-Routine', label: 'Daily Routines, Strengths and Needs' },
  { key: 'Family-Concern', label: 'Family Concerns, Priorities and Resources' },
  { key: 'Health-Eligibility', label: 'Health & Eligibility Summary' },
  { key: 'Developmental-Summary', label: 'Developmental Summary' },
];
const activeTabStyles: SxProps = {
  position: 'relative',
  backgroundColor: '#E4EEEE',

  borderLeft: '1px solid #008C82',
  borderRight: '1px solid #008C82',
  borderTop: '1px solid #008C82',
  borderBottom: 'none',

  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',

  // hides the parent bottom border only under active tab
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '-1px',
    height: '2px',
    backgroundColor: '#E4EEEE',
    zIndex: 1,
  },
};

const inactiveTabStyles: SxProps = {
  backgroundColor: '#CFDEDE',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
};

const IfspReportMainScreen = () => {
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabType>('Daily-Routine');
  const [pendingActiveTab, setPendingActiveTab] =
    useState<ActiveTabType | null>(null);
  const [employeeList, setEmployeeList] = useState<EmployeeListType[]>([
    {
      id: '',
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      disciplineType: '',
      disciplineHistoryList: [],
      phone: '',
      roles: [],
    },
  ]);
  const [reportCompletedBy, setReportCompletedBy] =
    useState<IfspReportCompletedByType>({
      ifspId: null,
      reportCompletedBy: {
        id: null,
        name: null,
        role: null,
        reportUpdated: false,
      },
    });
  const [originalReportCompletedByData, setOriginalReportCompletedByData] =
    useState<IfspReportCompletedByType>({
      ifspId: null,
      reportCompletedBy: {
        id: null,
        name: null,
        role: null,
        reportUpdated: false,
      },
    });
  const [reportCompletedByRole, setReportCompletedByRole] = useState<
    OptionType[]
  >([]);
  const [isReportCompletedByChanged, setIsReportCompletedByChanged] =
    useState<boolean>(false);

  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const dispatch = useAppDispatch();
  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspDailyRoutineReportDetails = useAppSelector(
    (state) =>
      state.ifspDailyRoutineReportDetails.originalIfspDailyRoutineReport,
  );
  const ifspFamilyConcernReportDetails = useAppSelector(
    (state) =>
      state.ifspFamilyConcernReportDetails.originalIfspFamilyConcernReport,
  );
  const ifspHealthSummaryReportDetails = useAppSelector(
    (state) =>
      state.ifspHealthSummaryReportDetails.originalIfspHealthSummaryReport,
  );
  const ifspDevelopmentSummaryReportDetails = useAppSelector(
    (state) =>
      state.ifspDevelopmentSummaryReportDetails
        .originalIfspDevelopmentSummaryReport,
  );
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const tabPositionRef = useRef(activeTab);

  const {
    isIfspReportChanged,
    isDailyRoutineCreatedLocally,
    isDevelopmentalSummaryChanged,
    isDevelopmentalSummaryCreatedLocally,
    isFamilyConcernsCreatedLocally,
    isFamilyConcernsChanged,
    isHealthSummaryCreatedLocally,
    isHealthSummaryChanged,
  } = useIfspUnsavedChanges();

  useEffect(() => {
    getIfspReportCompletedDetails().then((res) => {
      setOriginalReportCompletedByData(res);
      setReportCompletedBy(res);
      getEmployeeList(res.reportCompletedBy.id ? res.reportCompletedBy.id : '');
    });
  }, []);

  useEffect(() => {
    tabPositionRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const shouldUpdate =
      reportCompletedBy.reportCompletedBy.id !==
        originalReportCompletedByData.reportCompletedBy.id ||
      reportCompletedBy.reportCompletedBy.role !==
        originalReportCompletedByData.reportCompletedBy.role;

    setReportCompletedBy((prev) => {
      if (prev.reportCompletedBy.reportUpdated === shouldUpdate) {
        return prev;
      }

      return {
        ...prev,
        reportCompletedBy: {
          ...prev.reportCompletedBy,
          reportUpdated: shouldUpdate,
        },
      };
    });
    dispatch(
      updateIfspUnsavedFields({
        ...ifspUnsavedFields,
        isReporterFieldsChanged: shouldUpdate,
      }),
    );
  }, [
    reportCompletedBy.reportCompletedBy.id,
    reportCompletedBy.reportCompletedBy.role,
    originalReportCompletedByData.reportCompletedBy.id,
    originalReportCompletedByData.reportCompletedBy.role,
  ]);

  const handleReportCompletedByChange = () => {
    toggleLoader(true);
    getIfspReportCompletedDetails().then((res) => {
      setOriginalReportCompletedByData(structuredClone(res));
      setReportCompletedBy(structuredClone(res));
      getEmployeeList(res.reportCompletedBy.id ? res.reportCompletedBy.id : '');
      toggleLoader(false);
    });
  };

  const getEmployeeList = (reportCompletedById: string) => {
    const ids = [reportCompletedById];
    getAllEmployeeMinimalList(ids)
      .then((data) => {
        setEmployeeList(data);
        setReportCompletedByRole(() => {
          const filteredContactsRoles: OptionType[] = data
            .filter((contact) => contact.id === reportCompletedById)
            .flatMap((contact) =>
              contact.roles
                ? contact.roles.flatMap((roleString) =>
                    roleString.split(',').map((roleId) => ({
                      id: roleId.trim(),
                      label:
                        employeeRoles.find(
                          (empRole) => empRole.id === roleId.trim(),
                        )?.label ?? '',
                    })),
                  )
                : [],
            );
          return filteredContactsRoles;
        });
        setIsReportCompletedByChanged(false);
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.ReportCompleted.GetError');
        setToastrDefaultMessage('Failed to get Report Completed By list');
        setIsReportCompletedByChanged(false);
      });
  };

  const handleAddOrUpdateIfspReportCompletedDetails = () => {
    toggleLoader(true);
    addOrUpdateIfspReportCompletedDetails(reportCompletedBy)
      .then(() => {
        toggleLoader(false);
        getIfspReportCompletedDetails().then((res) => {
          setReportCompletedBy(res);
          setOriginalReportCompletedByData(res);
        });
        setOpen(true);
        setToastrVariable('success');
        setToastrDefaultMessage('Report Completed By updated successfully');
        setToastrId('IFSP.ReportCompleted.UpdateSuccess');
      })
      .catch(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.ReportCompleted.UpdateError');
        setToastrDefaultMessage('Failed to update Report Completed By');
      });
  };

  const ping = React.useCallback(() => {
    const currentTabPosition = tabPositionRef.current;
    let tabName:
      | 'Daily-Routine'
      | 'Family-Concern'
      | 'Developmental-Summary'
      | 'Health-Eligibility'
      | '' = '';
    if (currentTabPosition === 'Daily-Routine') {
      tabName = 'Daily-Routine';
    } else if (currentTabPosition === 'Family-Concern') {
      tabName = 'Family-Concern';
    } else if (currentTabPosition === 'Developmental-Summary') {
      tabName = 'Developmental-Summary';
    } else if (currentTabPosition === 'Health-Eligibility') {
      tabName = 'Health-Eligibility';
    }

    const referralId = localStorage.getItem('referralId');
    const userId = localStorage.getItem('userId');
    const pingjson = {
      userId: userId ?? '',
      referralId: referralId ?? '',
      tabName: tabName ?? '',
    };

    ifspHeartBeat(pingjson)
      .then((res: IfspHeartBeatType) => {
        dispatch(updateIfspHeartBeat(res));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    ping();
  }, [activeTab, ping]);

  useEffect(() => {
    const interval = setInterval(() => {
      ping();
    }, 20000);

    return () => {
      clearInterval(interval);
    };
  }, [ping]);

  const handleDownload = () => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const localTimezone = dayjs.tz.guess();
    if (clientId) {
      downloadIfspReport(clientId, localTimezone)
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });

          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `Individualized Family Service Plan (IFSP).pdf`,
          ); // Set desired file name here

          // Append anchor to document body
          document.body.appendChild(anchor);

          // Trigger anchor click
          anchor.click();

          window.open(blobUrl, '_blank');

          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);

          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrVariable('error');
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrVariable('error');
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ backgroundColor: '#E4EEEE' }} className="pb-xs">
      <SnackBarComponent
        open={open}
        handleClose={handleCloseSnakBar}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <ModalPopup
        open={openUnSavedModal}
        description="UnsavedAlertDescriptionWhileSwitching"
        labelId1="UnSavedAlertCancel"
        labelId2="UnsavedAlertSwitch"
        negativeActionLabel="No"
        positiveActionLabel="Yes"
        onCancel={() => {
          setOpenUnSavedModal(false);
        }}
        onOk={() => {
          if (activeTab === 'Daily-Routine') {
            dispatch(setIfspDailyRoutineReport(ifspDailyRoutineReportDetails));
          } else if (activeTab === 'Family-Concern') {
            dispatch(
              setIfspFamilyConcernReport(ifspFamilyConcernReportDetails),
            );
          } else if (activeTab === 'Developmental-Summary') {
            dispatch(
              setIfspDevelopmentSummaryReport(
                ifspDevelopmentSummaryReportDetails,
              ),
            );
          } else if (activeTab === 'Health-Eligibility') {
            dispatch(
              setIfspHealthSummaryReport(ifspHealthSummaryReportDetails),
            );
          }
          setActiveTab(pendingActiveTab as ActiveTabType);
          setOpenUnSavedModal(false);
          dispatch(resetIfspUnsavedFields());
        }}
      />

      <ModalPopup
        open={isReportCompletedByChanged}
        description="UnsavedAlertDescriptionWhileReset"
        labelId1="UnSavedAlertCancel"
        labelId2="ConsentReset"
        negativeActionLabel="No"
        positiveActionLabel="Yes"
        onCancel={() => {
          setIsReportCompletedByChanged(false);
        }}
        onOk={() => {
          handleReportCompletedByChange();
        }}
      />

      <Grid
        container
        direction={'row'}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        className="pl-md pr-md"
      >
        <Grid item xs={4} className="mt-lg">
          <AutocompleteSearch
            className="bg__white"
            labelId={'IFSP.reportCompletedBy'}
            defaultId="Report Completed By"
            optionHintId={'noemployeeDropdownTextId'}
            sxProps={{
              [`& .${autocompleteClasses.popupIndicator}`]: {
                transform: 'none',
              },
            }}
            data={employeeList.map((item) => ({
              id: item.id,
              label: item.firstName
                ? item.lastName + ', ' + item.firstName
                : '',
            }))}
            onSelectItem={(value: OptionType | null) => {
              if (value) {
                setReportCompletedBy({
                  ...reportCompletedBy,
                  reportCompletedBy: {
                    ...reportCompletedBy.reportCompletedBy,
                    id: value.id,
                    name: value.label,
                    role: '',
                  },
                });
                setReportCompletedByRole(() => {
                  const filteredContactsRoles: OptionType[] = employeeList
                    .filter((contact) => contact.id === value.id)
                    .flatMap((contact) =>
                      contact.roles
                        ? contact.roles.flatMap((roleString) =>
                            roleString.split(',').map((roleId) => ({
                              id: roleId.trim(),
                              label:
                                employeeRoles.find(
                                  (empRole) => empRole.id === roleId.trim(),
                                )?.label ?? '',
                            })),
                          )
                        : [],
                    );
                  return filteredContactsRoles;
                });
              } else {
                setReportCompletedBy({
                  ...reportCompletedBy,
                  reportCompletedBy: {
                    id: null,
                    name: null,
                    role: null,
                    reportUpdated: false,
                  },
                });
                setReportCompletedByRole([]);
              }
            }}
            selectedValue={{
              id: reportCompletedBy.reportCompletedBy.id || '',
              label:
                formatName(
                  employeeList.find(
                    (emp) => emp.id === reportCompletedBy.reportCompletedBy.id,
                  )?.firstName ?? '',
                  null,
                  employeeList.find(
                    (emp) => emp.id === reportCompletedBy.reportCompletedBy.id,
                  )?.lastName ?? '',
                  null,
                ) || '',
            }}
            disableLabel={false}
            disabled={!ifspPermission.hasClientEditPermission}
          />
        </Grid>
        <Grid item xs={4} className="mt-lg">
          <DropdownComponent
            names={reportCompletedByRole}
            labelid="IFSP.selectRoleNonMandatory"
            defaultlabelid="Select Role"
            value={reportCompletedBy.reportCompletedBy.role || ''}
            handleChange={(e) => {
              setReportCompletedBy((prevState) => ({
                ...prevState,
                reportCompletedBy: {
                  ...prevState.reportCompletedBy,
                  role: e,
                },
              }));
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
        </Grid>
        <Grid item xs={4} className="mt-lg">
          <Box component="div" className="flex__ justifyContent-FlexEnd mb-sm">
            <ButtonComponent
              className="btn-primary btn-cancel"
              labelId={'cancelText'}
              defaultLabelId="Cancel"
              variantType="outlined"
              onClick={() => {
                if (reportCompletedBy.reportCompletedBy.reportUpdated) {
                  setIsReportCompletedByChanged(true);
                } else {
                  setIsReportCompletedByChanged(false);
                }
              }}
              disabled={!ifspPermission.hasClientEditPermission}
            />
            <ButtonComponent
              className="btn-primary btn-submit  ml-md"
              labelId="IFSP.SaveReportCompletedBy"
              defaultLabelId="Save Report Completed By"
              variantType="contained"
              onClick={() => {
                handleAddOrUpdateIfspReportCompletedDetails();
              }}
              disabled={
                !ifspPermission.hasClientEditPermission ||
                !reportCompletedBy.reportCompletedBy.role ||
                !reportCompletedBy.reportCompletedBy.id ||
                !reportCompletedBy.reportCompletedBy.reportUpdated
              }
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        // className="flex__ justifyContentSpaceBtw alignItemCenter pr-xlg"
        sx={{ display: 'none' }}
      >
        <MediumTypography
          labelid="IFSP.Report"
          defaultlabel="Report"
          fontSize="18px"
          fontweight={600}
          className="pb-md pl-md pt-md"
        />
        <DownloadIcon className="cursorPointer" onClick={handleDownload} />
      </Box>

      <Box
        className="flex__ ml-md mr-md pt-md"
        gap={2}
        sx={{
          borderBottom: '1px solid #008C82',
          position: 'relative',
        }}
      >
        {tabs.map((tab) => (
          <Box
            className="cursorPointer"
            key={tab.key}
            onClick={() => {
              if (
                isIfspReportChanged ||
                (activeTab === 'Daily-Routine' &&
                  isDailyRoutineCreatedLocally) ||
                isDevelopmentalSummaryChanged ||
                (activeTab === 'Developmental-Summary' &&
                  isDevelopmentalSummaryCreatedLocally) ||
                isFamilyConcernsChanged ||
                (activeTab === 'Family-Concern' &&
                  isFamilyConcernsCreatedLocally) ||
                isHealthSummaryChanged ||
                (activeTab === 'Health-Eligibility' &&
                  isHealthSummaryCreatedLocally) ||
                ifspUnsavedFields.isOtherDailyRoutineFieldsChanged ||
                ifspUnsavedFields.isHealthAndEligibilityFieldsChanged ||
                ifspUnsavedFields.isOutComeProgressInfoFieldsChange ||
                ifspUnsavedFields.isServiceDeliveryFieldsChanged
              ) {
                setOpenUnSavedModal(true);
                setPendingActiveTab(tab.key);
              } else {
                setActiveTab(tab.key);
                setOpenUnSavedModal(false);
              }
            }}
            sx={activeTab === tab.key ? activeTabStyles : inactiveTabStyles}
          >
            <MediumTypography
              labelid={tab.label}
              defaultlabel={tab.label}
              fontSize="14px"
              fontweight={activeTab === tab.key ? 700 : 500}
              className="pb-md pl-md pt-md pr-md"
            />
          </Box>
        ))}
      </Box>
      {activeTab === 'Daily-Routine' && <DailyRoutineReport />}
      {activeTab === 'Family-Concern' && (
        <FamilyConcernsPrioritiesandResourcesReport />
      )}
      {activeTab === 'Developmental-Summary' && <DevelopmentSummaryReport />}
      {activeTab === 'Health-Eligibility' && <HealthEligibilitySummaryReport />}
    </Box>
  );
};

export default IfspReportMainScreen;
